import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { EmailTemplateDto } from '@core/services/dto';
import { SMSTemplateDto } from '@core/services/dto/sms-template.dto';
import { LinkModel, SearchEmailTemplatesOptionsModel } from '@shared/models';
import { SearchSMSTemplatesOptionsModel } from '@shared/models/search.sms-templates.options.model';
import { DATE_WITH_TIME_FORMAT, formatDateToLocalSimple } from '@shared/utils';

@Component({
  selector: 'app-email-template-list',
  templateUrl: './email-template-list.component.html',
  styleUrls: ['./email-template-list.component.scss']
})
export class EmailTemplateListComponent implements OnChanges, OnInit {
  @Input() templates: EmailTemplateDto[] | SearchEmailTemplatesOptionsModel[];
  @Input() templateSms: SMSTemplateDto[] | SearchSMSTemplatesOptionsModel[];
  @Input() mode: string;
  @Input() isSendDisabledParent = true;
  isSendDisabled: boolean;
  @Input() memberError = false;
  @Input() TemplateType: string;
  @Input() selectedLinks = [];
  @Input() emailAddresses: any[] = [];
  @Input() prospectEmailAddresses: any[] = [];
  memberlinks: any;
  isSentclicked = false;
  @Output() delete: EventEmitter<any> = new EventEmitter<any>();
  @Output() sendEmail: EventEmitter<EmailTemplateDto | SMSTemplateDto> = new EventEmitter<EmailTemplateDto | SMSTemplateDto>();
  @Input() isAutomaticEnabled = false;
  @Input() allowManualExecution = false;

  ngOnInit(): void {
    this.setDefaultChecked();
    this.updateSendButtonState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ✅ NEW: handle these inputs
    if (changes.isAutomaticEnabled || changes.allowManualExecution) {
      this.updateSendButtonState();
    }

    // Handle selectedLinks changes
    if (changes.selectedLinks && changes.selectedLinks.currentValue) {
      this.setDefaultChecked();
    }

    // Handle email data changes - re-merge when email data arrives
    if ((changes.emailAddresses || changes.prospectEmailAddresses) && this.memberlinks && this.memberlinks.length > 0) {
      this.mergeEmailData();
      this.updateSendButtonState();
    }

    // Handle template changes
    if (changes.templates && changes.templates.currentValue) {
      if (this.templates && this.templates.length && this.mode === 'view') {
        if (this.TemplateType === 'Email') {
          if (this.templates && Array.isArray(this.templates)) {
            (this.templates as EmailTemplateDto[]).forEach((item: EmailTemplateDto) => {
              item.sendDate = item.sendDate ? formatDateToLocalSimple(item.sendDate, DATE_WITH_TIME_FORMAT) : null;
            });
          }
        } else if (this.TemplateType === 'SMS') {
          if (this.templateSms && Array.isArray(this.templateSms)) {
            (this.templateSms as SMSTemplateDto[]).forEach((item: SMSTemplateDto) => {
              item.sendDate = item.sendDate ? formatDateToLocalSimple(item.sendDate, DATE_WITH_TIME_FORMAT) : null;
            });
          }
        }
      }
    }
  }

  send(event: EmailTemplateDto | SMSTemplateDto): void {
    const checkedMembers = this.memberlinks.filter(item => item.checked);

    this.isSentclicked = true;

    this.memberlinks = this.memberlinks.map(item => {
      if (item.checked) {
        item.isEmailSent = true;
      }
      return item;
    });

    this.updateSendButtonState();

    // Add checkedMembers to the event object
    const allEmailSent = this.memberlinks
      .filter(item => ['Member', 'Prospect'].includes(item.type))
      .every(item => item.isEmailSent === true);

    const updatedEvent = {
      ...event,
      checkedMembers,
      allEmailSent
    };

    this.sendEmail.emit(updatedEvent);
  }

  remove(event: EmailTemplateDto | SMSTemplateDto): void {
    this.delete.emit(event);
  }

  hasEmail(item: any): boolean {
    const hasEmailResult = !!(item.emailAddress || item.email || item.primaryEmail || item.Email || item.email_address);
    return hasEmailResult;
  }

  onSelectionChange(): void {
    this.updateSendButtonState();
  }

  onCheckboxChange(item: any) {
    item.checked = !item.checked;
    this.updateSendButtonState();
  }

  updateSendButtonState() {
    if (this.isAutomaticEnabled && !this.allowManualExecution) {
      this.isSendDisabled = true; // explicitly set
      return;
    }

    if (!this.memberlinks || this.memberlinks.length === 0) {
      this.isSendDisabled = true;
      return;
    }

    if (this.TemplateType === 'Email') {
      this.isSendDisabled = !this.memberlinks.some(
        item => item.checked && this.hasEmail(item) && !item.isEmailSent
      );
    }
  }

  private setDefaultChecked(): void {
    // Filter members/prospects first
    this.memberlinks = this.selectedLinks.filter(
      item => ['Member', 'Prospect'].includes(item.type)
    );

    // Merge email data into memberlinks
    this.mergeEmailData();

    // Check for already sent emails
    const sentMembers = this.memberlinks.filter(item => item.isEmailSent);
    if (sentMembers.length > 0) {
      // this.memberlinks = sentMembers;
      this.isSentclicked = true;
      this.isSendDisabled = true;
      this.memberlinks.forEach(item => {
        item.checked = !item.isEmailSent;
      });
    } else {
      // For new sends, auto-check first member with email
      const firstWithEmail = this.memberlinks.find(item => this.hasEmail(item));
      if (firstWithEmail) {
        firstWithEmail.checked = true;
      }
    }

    this.updateSendButtonState();
  }

  private mergeEmailData(): void {
    if (!this.memberlinks || this.memberlinks.length === 0) {
      console.log('No memberlinks to merge email data with');
      return;
    }

    console.log('Merging email data...');
    console.log('Member links before merge:', this.memberlinks);
    console.log('Email addresses:', this.emailAddresses);
    console.log('Prospect email addresses:', this.prospectEmailAddresses);

    this.memberlinks.forEach(member => {
      console.log('Processing member:', { type: member.type, ref: member.ref });

      // Find email data for members in separate arrays
      if (member.type === 'Member') {
        const emailData = this.emailAddresses ? this.emailAddresses.find(email => email.ref === member.ref) : null;
        console.log('Found member email data:', emailData);
        if (emailData && emailData.Email) {
          // Copy email properties to the member object
          member.Email = emailData.Email;
          member.email = emailData.Email;
          member.firstName = emailData.FirstName || member.firstName;
          member.lastName = emailData.LastName || member.lastName;
          member.phone = emailData.Phone;
          console.log('Member after merge:', member);
        } else {
          console.log('No email data found for member ref:', member.ref);
        }
      }
      // Find email data for prospects in separate arrays
      else if (member.type === 'Prospect') {
        const emailData = this.prospectEmailAddresses ? this.prospectEmailAddresses.find(email => email.ref === member.ref) : null;
        console.log('Found prospect email data:', emailData);
        if (emailData && emailData.Email) {
          // Copy email properties to the prospect object
          member.Email = emailData.Email;
          member.email = emailData.Email;
          member.firstName = emailData.FirstName || member.firstName;
          member.lastName = emailData.LastName || member.lastName;
          member.phone = emailData.Phone;
          console.log('Prospect after merge:', member);
        } else {
          console.log('No email data found for prospect ref:', member.ref);
        }
      }
    });

    console.log('Member links after merge:', this.memberlinks);
  }
}
